import { AxiosError, AxiosResponse } from 'axios'
import { camelize } from 'casing'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  errorLogger,
} from '@labrav/common'
import {
  getBuyersData,
  getBuyersJourneyContractsDataAPI,
  getBuyersJourneyDataAPI,
} from '../../api/marketplace/index'
import type { AppDispatch, RootState } from '../../store/types'
import {
  BuyersActionTypes,
  BuyersData,
  BuyerJourneyContracts,
  ContractType,
  JourneyContractDetails,
} from './types'

export { BuyersActionTypes }

export const setBuyerJourneyData = (journeyData: JourneyContractDetails) => ({
  type: BuyersActionTypes.SET_JOURNEY_DATA,
  payload: journeyData,
})

export const setBuyerContractsData = (
  contractsData: BuyerJourneyContracts[]
) => ({
  type: BuyersActionTypes.SET_BUYER_CONTRACTS_DATA,
  payload: contractsData,
})

export const getBuyer =
  ({ partnerId, buyerId }: { partnerId: string; buyerId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.BUYERS_OVERVIEW))
    try {
      const response = await getBuyersData({ partnerId, buyerId })
      const responseData = response?.data as { buyer?: unknown }
      const buyersData = camelize(
        responseData?.buyer ? { buyers: [responseData.buyer] } : {}
      )
      await dispatch(setBuyersData(buyersData as BuyersData))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.BUYERS_OVERVIEW))
    }
  }

export const getBuyersOverview =
  ({ partnerId, inputValue }: { partnerId: string; inputValue?: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.BUYERS_OVERVIEW))
    try {
      let response
      if (inputValue) {
        response = await getBuyersData({ partnerId, search: inputValue })
      } else {
        response = await getBuyersData({ partnerId })
      }
      const buyersData = camelize(response?.data ?? {})
      await dispatch(setBuyersData(buyersData as BuyersData))
    } catch (error: unknown) {
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.BUYERS_OVERVIEW))
    }
  }

export const getBuyersJourneyContractsData =
  ({
    partnerId,
    labraBuyerDetailsId,
  }: {
    partnerId: string
    labraBuyerDetailsId: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.BUYER_JOURNEY_CONTRACTS))
    try {
      const { data } = await getBuyersJourneyContractsDataAPI({
        partnerId,
        labraBuyerDetailsId,
      })
      const journeyData = data as { buyer_contracts?: unknown[] }
      const buyersJourneyData = camelize(journeyData?.buyer_contracts ?? [])
      await dispatch(
        setBuyerContractsData(buyersJourneyData as BuyerJourneyContracts[])
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.BUYER_JOURNEY_CONTRACTS))
    }
  }

export const getBuyersJourneyData =
  ({
    partnerId,
    labraBuyerDetailsId,
    contractId,
    contractType,
  }: {
    partnerId: string
    labraBuyerDetailsId: string
    contractId: string
    contractType: ContractType
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.BUYER_JOURNEY))
    try {
      const { data } = await getBuyersJourneyDataAPI({
        partnerId,
        labraBuyerDetailsId,
        contractId,
        contractType,
      })
      const journeyResponse = data as {
        buyer_contract_details?: unknown[]
      }
      const buyersJourneyData = camelize(
        journeyResponse?.buyer_contract_details?.[0]
      )
      await dispatch(
        setBuyerJourneyData(buyersJourneyData as JourneyContractDetails)
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.BUYER_JOURNEY))
    }
  }

export const clearBuyersOverView = () => ({
  type: BuyersActionTypes.CLEAR_BUYERS,
})

export const setBuyersData = (buyersData: BuyersData) => ({
  type: BuyersActionTypes.SET_BUYERS,
  payload: buyersData,
})
